import merge from 'deepmerge';
import { Locale } from 'locale-enum';
import { compact, first, flatMap, isEmpty, map, sortBy, take, values } from 'lodash';

import { ItemStatusEnum } from '@pimm/services/lib/store-inspections';
import { FormInputSources } from '../consts';
import { BaseReport, StoreFormReport } from './store-form-report';
import { formatName } from '../_helper';

namespace IChecklistReport {
  export type Item = Omit<BaseReport.Item, 'inputs'> & {
    dayparts: (Omit<BaseReport.Daypart, 'formGroups'> & { inputs: Record<string, BaseReport.Input[]> })[];
  };

  export type Section = Omit<BaseReport.Section, 'data'> & {
    data: Item[];
  };

  export type Form = Omit<BaseReport.Form, 'sections'> & {
    sections: Section[];
  };

  export type FormGroup = Omit<BaseReport.FormGroup, 'forms'> & {
    forms: Form[];
  };

  export type DaypartLeaders = Omit<BaseReport.Daypart, 'formGroups'>[];

  export type ReportDay = Omit<BaseReport.ReportDay, 'dayparts'> & {
    date: string;
    daypartLeaders: DaypartLeaders;
    formGroups: FormGroup[];
  };
}

export class ChecklistReport extends StoreFormReport {
  public composeReport(locale: Locale): any {
    this.locale = locale;
    const formVariants = [undefined, 'singleDaypart', 'dualDaypart', 'multiDaypart'];
    const reportDays = this.reportData?.reportDays ?? [];

    //#region Helpers

    /**
     * Merge two arrays of objects by a unique key.
     * If an object with the same key exists in both arrays,
     * it will be deeply merged using `deepmerge`.
     *
     * @param existing - The current array (accumulator).
     * @param incoming - The new array to merge in.
     * @param idKey - The property name that uniquely identifies an object.
     * @returns A new merged array of objects.
     */
    const mergeById = <T>(existing: T[], incoming: T[], idKey: keyof T): T[] => {
      const map = new Map<string, T>();

      // Store all existing entries in a Map for quick lookup by id
      existing.forEach((e: any) => map.set(e[idKey], e));

      incoming.forEach((i: any) => {
        const id = i[idKey];
        if (!id) return;

        if (map.has(id)) {
          // If already exists → deep merge incoming with existing
          map.set(id, merge(map.get(id)!, i, { arrayMerge }));
        } else {
          // If new → just insert
          map.set(id, i);
        }
      });

      return Array.from(map.values());
    };

    /**
     * Custom array merge strategy for deepmerge.
     * - If the array contains identifiable entities (by group/form/section/item id),
     *   merge them by ID instead of concatenating.
     * - If the array contains raw inputs, concatenate them.
     * - Otherwise, replace destination with source.
     */
    const arrayMerge = (dest: any[], source: any[], options: any): any[] => {
      if (dest.length && typeof dest[0] === 'object') {
        if ('packageDetailsGroupId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsGroupId');
        }
        if ('packageDetailsFormId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsFormId');
        }
        if ('packageDetailsSectionId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsSectionId');
        }
        if ('sectionLineItemId' in dest[0]) {
          return mergeById(dest, source, 'sectionLineItemId');
        }
        if ('inputs' in dest[0]) {
          // For input arrays, just concatenate (no merging by id)
          return dest.concat(source);
        }
      }
      // Default: replace with source
      return source;
    };

    /**
     * Flatten a form's sections into a flat list of items
     * and attach daypart metadata to each item.
     *
     * @returns A IChecklistReport.Form with items and dayparts attached.
     */
    const mapItemsByForm = (_daypart: Partial<BaseReport.Daypart>, _form: BaseReport.Form) => {
      const sections = map(_form.sections, ({ data = [], ..._section }) => {
        const section: IChecklistReport.Section = {
          ..._section,
          data: data.map(({ inputs: _itemInputs, ..._item }) => {
            // Normalize inputs: pick the last recorded input for each key
            const inputs = Object.entries(_itemInputs).reduce((inputs: Record<string, BaseReport.Input[]>, [inputId, _inputs]) => {
              return { ...inputs, [inputId]: take(_inputs, 1) }; // get the last recorded input
            }, {});
            const item: IChecklistReport.Item = {
              dayparts: [{ ..._daypart, inputs }],
              ..._item,
            };
            return item;
          }),
        };
        return section;
      });

      const form: IChecklistReport.Form = {
        ..._form,
        sections: sections,
      };
      return form;
    };

    //#endregion

    /**
     * STEP 1
     * Transform all reportDays by:
     *  - Flattening sections → items
     *  - Merging formGroups/forms/items across dayparts by ID
     *  - Attaching daypart metadata to each item
     *
     * @param reportDays - The raw BaseReport.ReportDay[]
     * @returns Flattened and merged IChecklistReport.ReportDay[]
     */
    const normalizedReportByDates = reportDays.reduce((_reportDays: IChecklistReport.ReportDay[], { dayparts, ..._reportDay }) => {
      const reportDay: IChecklistReport.ReportDay = {
        ..._reportDay,
        daypartLeaders: [],
        formGroups: [],
      };

      for (const { formGroups = [], ..._daypart } of dayparts) {
        if (!formGroups.length) continue;

        // Merge all dayparts for this reportDay
        const nextFormGroups = formGroups.map(({ forms = [], ..._formGroup }) => {
          return { forms: forms.map(_form => mapItemsByForm(_daypart, _form)), ..._formGroup };
        });

        // Merge with accumulated formGroups using custom arrayMerge
        reportDay.formGroups = merge(reportDay.formGroups, nextFormGroups, { arrayMerge });
        // Append daypart leader
        reportDay.daypartLeaders.push(_daypart);
      }

      return [..._reportDays, reportDay];
    }, []);

    /**
     * Step 2
     * Transform combined data into composer readable body
     */

    const reportDay = normalizedReportByDates[0];

    // TODO: create composer type
    const compiledData: Record<string, any> = {
      reports: [],
    };

    if (reportDay) {
      const reportBody: Record<string, any> = {
        ...this.composerBody(),
        formGroups: [],
        reviews: {},
        translations: {
          item: this.translate('item'),
          standard: this.translate('standard'),
          time: this.translate('time'),
          result: this.translate('result'),
          correctiveActions: this.translate('corrective_actions'),
          correctiveActionKeys: this.translate('corrective_action_keys'),
          completedBy: this.translate('completed_by'),
        },
      };

      // Page Leaders
      reportBody.reviews = {
        dayparts: reportDay.daypartLeaders.map(_ => {
          return this.composeReviewer({
            ..._.opsLeader,
            label: `${this.translate(_.label, _.translations)} - ${this.translate('ops_leader')}`,
          });
        }),
        generalManager: this.composeReviewer({
          ...reportDay.eodReview,
          useDefId: 'opsLeaderBorder',
          label: this.translate('general_manager'),
        }),
        districtManager: this.composeReviewer({
          ...reportDay.dmReview,
          label: this.translate('district_manager'),
        }),
      };

      // Page Body
      for (const { forms, ...formGroup } of sortBy(reportDay.formGroups, ['seq'])) {
        if (!forms?.length) continue;

        const numOfForms = forms.length;
        const inputTypes = forms.flatMap(({ sections }) => {
          return sections.flatMap(({ data }) => compact(data.flatMap(_ => _.inputType?.toLowerCase())));
        });
        const inputSources = FormInputSources.filter(_ => inputTypes.some(_inputType => _inputType.includes(_.keyword)));

        // define form group header
        reportBody.formGroups.push({
          useDefId: 'formGroupHeader',
          title: this.translate(formGroup.title, formGroup.titleTranslations),
          inputSourceLabel: !isEmpty(inputSources) ? this.translate('temperature_input_sources') : undefined,
          inputSources: inputSources.map(_source => ({ ..._source, label: this.translate(_source.label) })),
        });

        for (const { sections, ...form } of sortBy(forms, ['seq'])) {
          // Group items by section for checklist mode
          const sectionGroups = new Map<string, (typeof sections)[0]['data']>();

          for (const _section of sortBy(sections, ['seq'])) {
            const items = _section.data.filter(_ => _.status !== ItemStatusEnum.Excluded);
            const sectionTitle = this.translate(_section.title, _section.titleTranslations);

            if (!items.length) continue;

            if (!sectionGroups.has(sectionTitle)) {
              sectionGroups.set(sectionTitle, []);
            }
            sectionGroups.get(sectionTitle)!.push(...items);
          }

          // Add form header once per form (like opsbee)
          if (numOfForms > 1) {
            reportBody.formGroups.push({ useDefId: 'formHeader', title: this.translate(form.title, form.titleTranslations) });
          }

          // Process each section group
          for (const [sectionTitle, items] of sectionGroups) {
            if (!items.length) continue;

            // prettier-ignore
            const dayparts = items[0]?.dayparts.map(_ => ({ value: this.translate(_.label, _.translations) }))
            const numOfCols = dayparts.length;
            // find the variant using the number of dayparts. but if exceed, use the last variant or default to the first variant
            let variant = formVariants[numOfCols];

            if (!variant) {
              variant = numOfCols > formVariants.length - 1 ? formVariants.at(-1) : formVariants[1];
            }

            // Append Section Header
            reportBody.formGroups.push({
              useDefId: `${variant}Header`,
              title: sectionTitle,
              dayparts: dayparts,
            });

            // Append Item Row
            for (const { dayparts, ..._item } of items) {
              const item = this.composeItem(_item);

              reportBody.formGroups.push({
                ...item,
                useDefId: `${variant}Item`,
                dayparts: values(dayparts).map(_dp => {
                  const input = first(_dp.inputs?.[1]);
                  // prettier-ignore
                  const flattenInputs = values(_dp.inputs).map(_inputs => first(_inputs)).filter(_input => !!_input);
                  const _input = this.composeInput(flattenInputs, _item.inputType);

                  // Extract completedBy from user information (checklist report only)
                  const completedBy = formatName(input?.user?.firstName, input?.user?.lastName);

                  return { ..._input, inputSource: undefined, completedBy };
                }),
              });
            }
          }
        }
      }

      // Foot Notes
      const reportNotes = this.composeReportNotes(reportDays[0]);
      let formNotes: any[] = [];

      for (const _daypart of reportDays[0]?.dayparts) {
        const forms = compact(flatMap(_daypart.formGroups, _ => _.forms)).filter(_ => !!_.notes?.length);
        const notes = sortBy(forms, _ => this.translate(_.title, _.titleTranslations)).flatMap(_form =>
          this.composeFormNotes(_form, _daypart),
        );
        formNotes = formNotes.concat(notes);
      }
      // append report notes
      if (reportNotes.length) {
        reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('report_notes') });
        reportNotes.forEach(_def => reportBody.notes.push(_def));
      }
      // append form notes
      if (formNotes.length) {
        reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('form_notes') });
        formNotes.forEach(_def => reportBody.notes.push(_def));
      }

      compiledData.reports.push(reportBody);
    }

    return compiledData.reports[0];
  }
}
